/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import List, { expandEventTypes, activeEventTypes } from '@ba-ui-toolkit/list';
import { lodash, listHelper, propsUtils } from '@ba-ui-toolkit/utils';

const { sortBy, forOwn, findIndex } = lodash;
const { multiSelect, singleSelect } = listHelper;
const { sanitize, getInheritedProps } = propsUtils;

function purge(byIdData, byIdMap) {
	forOwn(byIdData, (v, key) => {
		if (!byIdMap[key]) {
			delete byIdData[key];
		}
	});
}

function toBoolMap(byIdObjects) {
	return Object.keys(byIdObjects).reduce((map, id) => {
		map[id] = !!byIdObjects[id];

		return map;
	}, {});
}
function computePathToRoot(node) {
	let result = '';
	let p = node;
	do {
		result = result ? `${p.relativeIndex}_${result}` : `${p.relativeIndex}`;
		p = p.parent;
	} while (p);

	return result;
}

export default class Tree extends Component {
	static propTypes = {
		...sanitize(List.propTypes, null, 'length'),
		onActiveChange: PropTypes.func,
		getChildren: PropTypes.func.isRequired,
		getId: PropTypes.func.isRequired,
		getRootNodes: PropTypes.func,
		getHasChildren: PropTypes.func,
		onExpand: PropTypes.func,
		isVisible: PropTypes.func,
		isAlwaysExpanded: PropTypes.func,
		onSelectionChange: PropTypes.func,
		onMenu: PropTypes.func,
		filter: PropTypes.string,
		data: PropTypes.any.isRequired,
		selectedNodes: PropTypes.object,
		defaultSelectedNodes: PropTypes.object,
		expandedNodes: PropTypes.object,
		defaultExpandedNodes: PropTypes.object,
		selectionMode: PropTypes.oneOf(['none', 'disabled', 'single', 'multi']),
		activeNode: PropTypes.any,
		className: PropTypes.string,
		style: PropTypes.object,
		emptyFilterState: PropTypes.node,
		expandedByDefault: PropTypes.bool,
		focusNode: PropTypes.object,
		itemSizeGetter: PropTypes.func,
		itemSizeEstimator: PropTypes.func,
		/** Specifies the maximum height of the tree */
		maxHeight: PropTypes.number,
	};

	static defaultProps = {
		getRootNodes: nodes => (Array.isArray(nodes) ? nodes : [nodes]),
		getChildren: node => node.children,
		getId: node => node.id,
		isAlwaysExpanded: () => false,
		isVisible: () => true,
		className: '',
		style: {},
	};

	state = {
		filterCollapsedNodes: {},
		active: this.props.activeNode ? this.props.getId(this.props.activeNode) : null,
	};

	componentWillMount() {
		const {
			defaultSelectedNodes,
			selectedNodes,
			defaultExpandedNodes,
			expandedNodes,
		} = this.props;
		const selected = toBoolMap(selectedNodes || defaultSelectedNodes || {});
		const expanded = toBoolMap(expandedNodes || defaultExpandedNodes || {});

		this.setState({ expandedNodes: expanded, selectedNodes: selected });
	}

	componentWillReceiveProps(nextProps) {
		const { filter, selectedNodes, expandedNodes, activeNode } = nextProps;

		const newState = {};

		if (filter !== this.props.filter) {
			newState.filterCollapsedNodes = {};
		}

		if (selectedNodes) {
			newState.selectedNodes = toBoolMap(selectedNodes);
		}

		if (expandedNodes) {
			newState.expandedNodes = toBoolMap(expandedNodes);
		}

		if (activeNode) {
			newState.active = this.props.getId(activeNode);
		}

		this.setState(newState);
	}

	componentWillUpdate(nextProps, nextState) {
		if (nextState.expandedNodes !== this.state.expandedNodes) {
			Object.keys(nextState.expandedNodes).forEach(id => {
				this._prevExpandedNodes[id] = true;
			});
		}
	}

	componentDidUpdate(prevProps, prevState) {
		const byIdMap = this._byIdMap;
		const { expandedNodes, filterCollapsedNodes, selectedNodes } = this.state;

		purge(this._prevExpandedNodes, byIdMap);
		purge(selectedNodes, byIdMap);
		purge(expandedNodes, byIdMap);
		purge(filterCollapsedNodes, byIdMap);

		if (prevState.active !== this.state.active) {
			this._list.scrollAround(this._activeIndex);
		}
	}

	// returns the nodes in the order in which they appear in the tree
	getOrderedSelectedNodes = (selectedNodes = this.state.selectedNodes) => {
		const selectedMap = this._toObjectMap(selectedNodes);

		return sortBy(Object.keys(selectedMap), id => this._byIdMap[id].index).map(
			id => selectedMap[id]
		);
	};

	// returns the nodes in the order in which they were selected
	// caveat: id needs to be a non-integer for this to work properly.
	getSelectedNodes = () => {
		const selectedMap = this._toObjectMap(this.state.selectedNodes);

		return Object.keys(selectedMap).map(id => selectedMap[id]);
	};

	_activeIndex = -1;

	_pivotIndex = -1;

	_lastClickIndex = -1;

	_byIdMap = null;

	_data = null;

	_prevExpandedNodes = {};

	_getParentIndex = index => {
		const id = this._getId(index);
		const { parent } = this._byIdMap[id];

		if (!parent) {
			return -1;
		}

		return this._data.indexOf(parent);
	};

	_isExpandedByIndex = index => {
		const { filter } = this.props;
		const { filterCollapsedNodes } = this.state;

		const id = this._getId(index);

		return filter ? !filterCollapsedNodes[id] : this._isExpandedById(id);
	};

	_isExpandedById = id => {
		const { expandedByDefault } = this.props;
		const { expandedNodes } = this.state;

		return expandedByDefault
			? !Object.prototype.hasOwnProperty.call(expandedNodes, id) || expandedNodes[id]
			: expandedNodes[id];
	};

	_isSelected = index => this.state.selectedNodes[this._getId(index)];

	_itemRenderer = (index, props) => {
		const treeNode = this._data[index];
		const { focusDepth, hasChildren, alwaysExpanded, visible, node } = treeNode;
		const expanded = this._isExpandedByIndex(index);
		const { itemRenderer, data, focusNode, filter } = this.props;

		const treeProps = {
			...props,
			node,
			hasChildren,
			alwaysExpanded,
			depth: focusDepth,
			expanded,
			visible,
			data,
			pathToRoot: computePathToRoot(treeNode),
			isFocusNode: !filter && focusNode && focusNode === node,
		};

		return itemRenderer(node, treeProps);
	};

	_onActiveChange = event => {
		this.setState(() => {
			const { index, type } = event;
			const { onActiveChange } = this.props;
			let newState;

			if (type === activeEventTypes.ABSOLUTE) {
				const active = this._getId(index);
				newState = { active };
			} else {
				const delta = type === activeEventTypes.UP ? -1 : 1;
				const active = this._getId(this._activeIndex + delta);

				newState = { active };
			}

			onActiveChange &&
				onActiveChange(newState.active ? this._byIdMap[newState.active].node : null);

			return newState;
		});
	};

	_onMenu = (x, y, e) => this.props.onMenu(x, y, this._toObjectMap(this.state.selectedNodes), e);

	_onExpand = event => {
		const { index, type } = event;
		const { onExpand, data, filter } = this.props;
		const treeNode = this._data[index];
		const activeNode = this._byIdMap[this.state.active];
		const { id } = treeNode;

		let expanded;

		if (type === expandEventTypes.TOGGLE) {
			expanded = !this.state.expandedNodes[id];
		} else if (type === expandEventTypes.EXPAND) {
			expanded = true;
		} else if (type === expandEventTypes.COLLAPSE) {
			expanded = false;
		}

		if (!expanded && activeNode) {
			let inHierarchy = false;
			let node = activeNode;
			while (node && !inHierarchy) {
				inHierarchy = node.parent === treeNode;
				node = node.parent;
			}

			if (inHierarchy) {
				this.setState({ active: id, pivot: id, lastClick: id });
			}
		}

		const expandedNodes = { ...this.state.expandedNodes, [id]: expanded };
		const filterCollapsedNodes = { ...this.state.filterCollapsedNodes, [id]: !expanded };

		if (!this.props.expandedNodes) {
			this.setState({ expandedNodes, filterCollapsedNodes });
		} else if (filter) {
			this.setState({ filterCollapsedNodes });
		}

		onExpand && onExpand(expandedNodes, [treeNode.node], expanded, data);
	};

	_onSelectionChange = event => {
		const { data, filter, selectionMode } = this.props;
		const { selectedNodes } = this.state;

		let selectedMap = {};
		if (selectionMode === 'single') {
			const key = singleSelect(event, this._data, Object.keys(selectedNodes)[0], this._getId);
			if (key != null) {
				selectedMap = {
					[key]: true,
				};
			}
		} else {
			selectedMap = multiSelect(event, this._data, selectedNodes, this._getId).selectedMap;
		}

		if (!this.props.selectedNodes) {
			this.setState({ selectedNodes: toBoolMap(selectedMap) });
		}

		if (filter) {
			const nodes = [];
			const expandedNodes = { ...this.state.expandedNodes };
			Object.keys(selectedMap).forEach(id => {
				let node = this._byIdMap[id].parent;
				while (node) {
					if (!expandedNodes[node.id]) {
						expandedNodes[node.id] = true;
						nodes.push(node);
					}
					node = this._byIdMap[node.id].parent;
				}
			});

			if (nodes.length) {
				if (!this.props.expandedNodes) {
					this.setState({ expandedNodes });
				}

				const { onExpand } = this.props;
				onExpand &&
					onExpand(
						expandedNodes,
						nodes.map(n => n.node),
						true,
						data
					);
			}
		}

		if (event.pivot && event.index !== -1) {
			this.setState({ pivot: this._getId(event.index) });
		}

		if (event.last != null) {
			this.setState({ lastClick: this._getId(event.last) });
		}

		const { onSelectionChange } = this.props;

		onSelectionChange &&
			onSelectionChange(
				this._toObjectMap(selectedMap),
				this.getOrderedSelectedNodes(selectedMap),
				data
			);
	};

	_toFlat(
		nodes,
		byIdMap,
		flatData,
		depth = 0,
		focusDepth = 0,
		startingIndex = 0,
		parent = null,
		isFocusChild = false
	) {
		const {
			getId,
			getChildren,
			getHasChildren,
			isVisible,
			filter,
			isAlwaysExpanded,
			focusNode,
		} = this.props;
		const { filterCollapsedNodes } = this.state;
		const prevExpandedNodes = this._prevExpandedNodes;

		let index = startingIndex;

		for (let i = 0; i < nodes.length; i++) {
			const node = nodes[i];
			const id = getId(node);

			let hasChildren;
			let children;

			if (getHasChildren) {
				hasChildren = getHasChildren(node, depth, focusDepth);
			} else {
				children = getChildren(node, depth, focusDepth);
				hasChildren = !!(children && children.length);
			}

			const alwaysExpanded = isAlwaysExpanded(node, depth, i, nodes.length, focusDepth);
			const treeNode = {
				relativeIndex: i,
				node,
				id,
				depth,
				focusDepth,
				index,
				parent,
				hasChildren,
				visible: true,
				alwaysExpanded,
			};
			index += 1;
			byIdMap[id] = treeNode;

			if (filter) {
				const visible = isVisible(filter, node);
				treeNode.visible = isVisible(filter, node);

				if (hasChildren) {
					children = children || getChildren(node, depth, focusDepth) || [];
					const flatChildren = [];
					this._toFlat(
						children,
						byIdMap,
						flatChildren,
						depth + 1,
						depth + 1,
						index,
						treeNode
					);
					index += flatChildren.length;
					const filterCollapsed = filterCollapsedNodes[id];

					if (flatChildren.length || visible) {
						flatData.push(treeNode);
					}

					if (flatChildren.length && !filterCollapsed) {
						flatData.push(...flatChildren);
					}
				} else if (visible) {
					flatData.push(treeNode);
				}
			} else {
				const inFocus = !focusNode || focusNode === node || isFocusChild;

				if (flatData && inFocus) {
					flatData.push(treeNode);
				}

				if (hasChildren) {
					const expanded = this._isExpandedById(id) || alwaysExpanded;
					const prevExpanded = prevExpandedNodes[id];

					if (expanded || prevExpanded) {
						children = children || getChildren(node, depth, focusDepth) || [];
						// If we were previously expanded we need to go down this path in order to generate the byIdMap
						// to fully purge data that is no longer accessible.
						// `null` is passed in for `flatData` so that we do not actually add nodes to your flat data.
						const length = flatData ? flatData.length : 0;
						this._toFlat(
							children,
							byIdMap,
							expanded ? flatData : null,
							depth + 1,
							focusDepth + (inFocus ? 1 : 0),
							index,
							treeNode,
							inFocus
						);
						index += flatData ? flatData.length - length : 0;
					}
				}
			}
		}
	}

	_toObjectMap(boolMap) {
		return Object.keys(boolMap).reduce((map, id) => {
			if (boolMap[id] && this._byIdMap[id]) {
				map[id] = this._byIdMap[id].node;
			}

			return map;
		}, {});
	}

	_refList = list => {
		this._list = list;
	};

	_getIndex = id => (id == null ? -1 : findIndex(this._data, treeNode => id === treeNode.id));

	_getId = index => (this._data[index] ? this._data[index].id : null);

	_itemSizeEstimator = (index, cache) =>
		this.props.itemSizeEstimator(this._data[index].node, index, cache);

	_itemSizeGetter = index => this.props.itemSizeGetter(this._data[index].node, index);

	scrollToId(id) {
		const index = this._getIndex(id);
		this._list.scrollTo(index);
	}

	render() {
		const {
			onMenu,
			data,
			getRootNodes,
			className,
			style,
			filter,
			emptyFilterState,
			itemSizeGetter,
			itemSizeEstimator,
			maxHeight,
			...others
		} = this.props;

		const byIdMap = {};
		const flatData = [];

		this._toFlat(getRootNodes(data), byIdMap, flatData);

		this._data = flatData;
		this._byIdMap = byIdMap;

		this._activeIndex = this._getIndex(this.state.active);
		this._pivotIndex = this._getIndex(this.state.pivot);
		this._lastClickIndex = this._getIndex(this.state.lastClick);

		const listPassthroughProps = getInheritedProps(others, List);

		if (emptyFilterState && filter && flatData.length === 0) {
			return emptyFilterState;
		}

		return (
			<List
				virtualized
				data-tid="ba-tree__list"
				{...sanitize(others, Tree)}
				{...listPassthroughProps}
				ref={this._refList}
				activeIndex={this._activeIndex}
				pivotIndex={this._pivotIndex}
				lastClickIndex={this._lastClickIndex}
				length={this._data.length}
				onExpand={this._onExpand}
				getParentIndex={this._getParentIndex}
				role="tree"
				itemRenderer={this._itemRenderer}
				isExpanded={this._isExpandedByIndex}
				onMenu={onMenu && this._onMenu}
				onSelectionChange={this._onSelectionChange}
				isSelected={this._isSelected}
				onActiveChange={this._onActiveChange}
				getKey={this._getId}
				getItemId={this._getId}
				hierarchical
				className={className}
				style={{ maxHeight, ...style }}
				itemSizeEstimator={itemSizeEstimator && this._itemSizeEstimator}
				itemSizeGetter={itemSizeGetter && this._itemSizeGetter}
			/>
		);
	}
}
